import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { articlesAPI, categoriesAPI } from '../lib/api';
import { Layout } from '../components/layout/Layout';
import { ArticleCard } from '../components/articles/ArticleCard';
import { Skeleton } from '../components/ui/skeleton';

const CATEGORY_IMAGES = {
  film: 'https://images.unsplash.com/photo-1579912437184-595291124a34?w=1920&h=600&fit=crop',
  music: 'https://images.unsplash.com/photo-1648260029310-5f1da359af9d?w=1920&h=600&fit=crop',
  tv: 'https://images.unsplash.com/photo-1573717626258-e9d6879998f0?w=1920&h=600&fit=crop',
  sports: 'https://images.unsplash.com/photo-1759694468344-b5fffb9e6341?w=1920&h=600&fit=crop',
  business: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1920&h=600&fit=crop',
};

export const CategoryPage = () => {
  const { slug } = useParams();
  const [category, setCategory] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 12;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch category info
        const catRes = await categoriesAPI.getBySlug(slug);
        setCategory(catRes.data);

        // Fetch articles
        const articlesRes = await articlesAPI.getPublished({ 
          category_slug: slug, 
          limit, 
          skip: page * limit 
        });
        setArticles(articlesRes.data.articles);
        setTotal(articlesRes.data.total);
      } catch (error) {
        console.error('Error fetching category:', error);
        // Set fallback category
        setCategory({
          name: slug.charAt(0).toUpperCase() + slug.slice(1),
          slug: slug,
          description: `Latest ${slug} news and updates`
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug, page]);

  const categoryImage = CATEGORY_IMAGES[slug] || CATEGORY_IMAGES.entertainment;
  const totalPages = Math.ceil(total / limit);

  // Create placeholder articles
  const placeholderArticles = Array.from({ length: 6 }, (_, i) => ({
    id: `placeholder-${i}`,
    slug: `placeholder-${slug}-${i}`,
    title: `${category?.name || slug} Story Coming Soon`,
    subheadline: 'Check back for the latest updates.',
    author: 'The Trade Media',
    created_at: new Date().toISOString(),
    hero_image: CATEGORY_IMAGES[slug],
    category: category
  }));

  return (
    <Layout>
      {/* Category Header — clean text, no photo */}
      <section className="bg-foreground text-background py-12 md:py-16" data-testid="category-hero">
        <div className="container-custom">
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl font-bold mb-3">
            {category?.name || slug}
          </h1>
          {category?.description && (
            <p className="text-background/60 text-lg md:text-xl max-w-xl">
              {category.description}
            </p>
          )}
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-12" data-testid="category-articles">
        <div className="container-custom">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="space-y-4">
                  <Skeleton className="aspect-video w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {(articles.length ? articles : placeholderArticles).map((article) => (
                  <ArticleCard 
                    key={article.id} 
                    article={article} 
                    showCategory={false}
                  />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center gap-2 mt-12">
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i}
                      onClick={() => setPage(i)}
                      className={`w-10 h-10 rounded-sm font-medium transition-colors ${
                        page === i 
                          ? 'bg-foreground text-background' 
                          : 'bg-secondary hover:bg-secondary/80'
                      }`}
                      data-testid={`page-${i + 1}`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default CategoryPage;
